/**
 * Incremental SSE frame parser for XHR streaming (axios onDownloadProgress).
 * Handles partial frames and CRLF normalization — important for MS Edge and
 * proxies that buffer or deliver chunks differently than Chrome.
 */

export class SSEBuffer {
  private buffer = ''

  /** Feed new bytes; returns `data:` payload strings from complete SSE frames. */
  feed(chunk: string): string[] {
    this.buffer = `${this.buffer}${chunk}`.replace(/\r\n/g, '\n')
    const payloads: string[] = []
    let idx: number
    while ((idx = this.buffer.indexOf('\n\n')) >= 0) {
      const block = this.buffer.slice(0, idx)
      this.buffer = this.buffer.slice(idx + 2)
      const payload = this.extractDataPayload(block)
      if (payload !== null) {
        payloads.push(payload)
      }
    }
    return payloads
  }

  /** Parse any trailing partial frame when the stream completes. */
  flush(): string[] {
    const payloads: string[] = []
    if (!this.buffer.trim()) {
      return payloads
    }
    this.buffer = this.buffer.replace(/\r\n/g, '\n')
    const payload = this.extractDataPayload(this.buffer)
    if (payload !== null) {
      payloads.push(payload)
    }
    this.buffer = ''
    return payloads
  }

  private extractDataPayload(block: string): string | null {
    const line = block.split('\n').find((l) => l.trimStart().startsWith('data:'))
    if (!line) {
      return null
    }
    const dataLine = line.trimStart()
    const jsonStr = dataLine.slice(5).trim()
    if (jsonStr === '') {
      return null
    }
    return jsonStr
  }
}

export interface XHRStreamState {
  seen: number
  buffer: SSEBuffer
  lastText: string
}

export function createXHRStreamState(): XHRStreamState {
  return {
    seen: 0,
    buffer: new SSEBuffer(),
    lastText: '',
  }
}

/** Read accumulated response text from an axios XHR progress event. */
export function getXHRResponseText(progressEvent: unknown): string {
  const pe = progressEvent as {
    event?: { target?: XMLHttpRequest; currentTarget?: XMLHttpRequest }
    target?: XMLHttpRequest
  }
  const xhr =
    pe?.event?.target
    ?? pe?.event?.currentTarget
    ?? pe?.target
  return xhr?.responseText ?? xhr?.response ?? ''
}

/**
 * Feed new XHR response bytes into the SSE buffer.
 * When `flush` is true, also emits any trailing partial frame (call after request completes).
 */
export function feedXHRStreamProgress(
  state: XHRStreamState,
  responseText: string,
  flush = false,
): string[] {
  state.lastText = responseText
  const payloads: string[] = []

  if (responseText.length > state.seen) {
    const chunk = responseText.slice(state.seen)
    state.seen = responseText.length
    payloads.push(...state.buffer.feed(chunk))
  }

  if (flush) {
    payloads.push(...state.buffer.flush())
  }

  return payloads
}
